<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import type { CombinedError } from '@urql/core';

const props = withDefaults(defineProps<{
  error: CombinedError,
  class?: HTMLAttributes['class'],
  focus?: boolean,
}>(), {
  focus: true,
});

const errors = computed(() => {
  const rawErrors = toRaw(props.error);
  return rawErrors?.graphQLErrors && rawErrors.graphQLErrors.length > 0
    ? rawErrors.graphQLErrors[0]
    : undefined;
});

const messageError = computed(() => ((errors.value?.extensions && 'debugMessage' in errors.value.extensions)
  ? `${errors.value.message} (${errors.value.extensions.debugMessage})`
  : errors.value?.message
));
const validationError = computed(
  () => errors.value?.extensions?.validation as Record<string, string[]>,
);

// Focus if the error is visible and the focus prop is true
const errorId = computed(() => `error-${useId()}`);
const onFocus = () => {
  if (props.focus) {
    document.getElementById(errorId.value)?.focus();
  }
};
watch(() => props.focus, onFocus);
onMounted(onFocus);
</script>

<template>
  <slot :validation-error="validationError" :message-error="messageError" :error-id="errorId">
    <UAlert
      :id="errorId"
      color="error"
      variant="soft"
      :class="$props.class"
      tabindex="-1"
      role="alert"
      data-cy="cy-error-alert"
    >
      <template #title>
        <UiErrorValidation v-if="validationError" :errors="validationError" />
        <div v-else>
          {{ messageError }}
        </div>
      </template>
    </UAlert>
  </slot>
</template>
